import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  Gateway,
  GatewayCommands,
  GatewayFirmwareCommands,
  GatewayFirmwareProperties,
  GatewayProperties,
  InvitationStatusType,
  SignalProperties,
} from '@vi-iot/client';

import {
  FeatureService,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  InvitationsStore,
  RemoteConfigStore,
} from '@store';

import { SUFFIX } from '../../../../shared/common/units';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-gateway-details-overlay',
  templateUrl: './gateway-details-overlay.component.html',
  styleUrls: ['./gateway-details-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GatewayDetailsOverlayComponent implements OnInit, OnDestroy {
  @Input() gatewaySerial: string;
  @Input() installationId: number;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  wifi$: Observable<Feature<SignalProperties, never>>;
  status$: Observable<Feature<GatewayProperties, GatewayCommands>>;
  gatewayFirmwareVersion$: Observable<Feature<GatewayFirmwareProperties, GatewayFirmwareCommands>>;
  acceptedDateInvitation$: Observable<string>;
  isFirmwareOutdated$: Observable<boolean>;
  gatewayType$: Observable<string>;
  gateway: Gateway;
  SUFFIX = SUFFIX;

  readonly featurePropertyKey = FeaturePropertyKey;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    protected gatewayStore: GatewayStore,
    public installationDetailsStore: InstallationDetailsStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    private featureService: FeatureService,
    private invitationsStore: InvitationsStore,
    private remoteConfigStore: RemoteConfigStore,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.invitationsStore.getUserInvitations({
      installationId: this.installationId,
      status: InvitationStatusType.Accepted,
      expanded: true,
    });

    this.wifi$ = this.installationFeaturesStore.selectInstallationTCUFeature(
      FeatureNames.tcuWifi,
      this.gatewaySerial
    );
    this.status$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayStatus
    );
    this.acceptedDateInvitation$ = this.invitationsStore.acceptedDate$;
    this.isFirmwareOutdated$ = this.installationDetailsStore.isFirmwareUpToDate$.pipe(
      withLatestFrom(this.remoteConfigStore.azureCertificateFirmwareCheck$),
      map(
        ([isFirmwareUpToDate, azureCertificateFirmwareCheck]) =>
          !isFirmwareUpToDate && azureCertificateFirmwareCheck
      )
    );
    this.gatewayFirmwareVersion$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayFirmware
    );

    this.featureService
      .selectGatewayProperties()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        this.gateway = data;
      });

    this.gatewayType$ = this.gatewayStore.getGatewayType();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
